import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { getAvexaClient, isAvexaConfigured } from '@/lib/avexa'

import {
  AdminSessionContext,
  AdminSignInError,
  type AdminDeniedReason,
  type AdminSessionStatus,
  type AdminSessionValue,
  type AdminSignInErrorCode,
} from './session'

/**
 * Resolves the Supabase session, asks the DATABASE whether that account is an
 * admin, and publishes the answer to the route guard.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ONE RULE THIS FILE EXISTS TO ENFORCE: ADMIN STATUS IS NEVER A CLIENT-SIDE
 * CLAIM.
 *
 * Not a field on the user, not a JWT claim, not a flag in localStorage — every
 * one of those is editable by whoever holds the browser, and the panel would
 * then be "secured" by something the attacker controls. It is one round trip to
 * `public.is_admin()`, which is `SECURITY DEFINER` over `admin_users` precisely
 * so the answer cannot be reached any other way.
 *
 * This is also only HALF the protection, and deliberately so. The guard decides
 * what renders; RLS decides what the database will do. Docs/08: "Route guards
 * AND RLS both enforce this. Never one without the other." A guard alone hides
 * buttons; it does not stop a request.
 *
 * ⚠️ TODAY `is_admin()` RETURNS FALSE FOR EVERY ACCOUNT, and that is correct
 * behaviour rather than a bug in this file. `admin_users` has not been created
 * yet — it is migration 014-onward work with the rest of the admin tables — and
 * the function opens with:
 *
 *     if to_regclass('public.admin_users') is null then return false; end if;
 *
 * So a real, valid sign-in lands on the no-access screen until that table exists
 * and has a row. The screen says exactly that, because "you are not an admin" and
 * "the admin table does not exist yet" look identical from the browser and an
 * operator must not be left guessing which one they are looking at.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * Ask the database. Throws rather than returning false on failure: a network
 * error is not the same answer as "no", and treating it as one would tell a
 * genuine admin they had been removed.
 */
async function fetchIsAdmin(): Promise<boolean> {
  // `is_admin(p_roles admin_role[] default null)` — no argument means "any
  // active admin role". There is no named wrapper for it in `@avexa/api` yet;
  // when one lands this becomes a call to that instead.
  const { data, error } = await getAvexaClient().rpc('is_admin')
  if (error) throw new Error(error.message)
  // `data` is typed `never` while `packages/types/src/database.ts` is still the
  // generator's placeholder (CLAUDE.md § 8), so it is narrowed through
  // `unknown` rather than cast. Anything but a literal `true` is not a yes.
  const answer: unknown = data
  return answer === true
}

function classifySignInError(error: { message: string; code?: string }): AdminSignInErrorCode {
  // Newer auth-js sends a stable `code`; older builds only send prose. Both are
  // checked, because the panel must not degrade to "something went wrong" on a
  // wrong password — the commonest failure of all.
  const code = error.code ?? ''
  const message = error.message.toLowerCase()

  if (code === 'invalid_credentials' || message.includes('invalid login credentials')) {
    return 'invalid-credentials'
  }
  if (code === 'email_not_confirmed' || message.includes('email not confirmed')) {
    return 'email-not-confirmed'
  }
  if (code === 'over_request_rate_limit' || message.includes('rate limit')) {
    return 'rate-limited'
  }
  return 'unknown'
}

type State = {
  status: AdminSessionStatus
  userId: string | null
  email: string | null
  deniedReason: AdminDeniedReason | null
  errorMessage: string | null
}

const LOADING: State = {
  status: 'loading',
  userId: null,
  email: null,
  deniedReason: null,
  errorMessage: null,
}

const SIGNED_OUT: State = {
  status: 'signed-out',
  userId: null,
  email: null,
  deniedReason: null,
  errorMessage: null,
}

export function AdminSessionProvider({ children }: { children: ReactNode }): ReactNode {
  const [state, setState] = useState<State>(LOADING)
  const queryClient = useQueryClient()

  /**
   * Every resolution is stamped with the generation that started it, and a
   * stale one is dropped. Sign-out during an in-flight `is_admin()` is the
   * exact race that otherwise authorises a session that no longer exists.
   */
  const generation = useRef(0)
  const [recheckToken, setRecheckToken] = useState(0)

  useEffect(() => {
    if (!isAvexaConfigured()) {
      setState({
        status: 'unconfigured',
        userId: null,
        email: null,
        deniedReason: null,
        errorMessage: 'VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY',
      })
      return
    }

    let disposed = false

    const resolve = async (
      user: { id: string; email?: string | undefined } | null,
      mine: number,
    ): Promise<void> => {
      if (user === null) {
        if (!disposed && generation.current === mine) setState(SIGNED_OUT)
        return
      }

      const email = user.email ?? null

      try {
        const isAdmin = await fetchIsAdmin()
        if (disposed || generation.current !== mine) return
        setState({
          status: isAdmin ? 'authorized' : 'denied',
          userId: user.id,
          email,
          deniedReason: isAdmin ? null : 'not-admin',
          errorMessage: null,
        })
      } catch (error) {
        if (disposed || generation.current !== mine) return
        // Fail CLOSED. An unanswerable check is never an authorisation — but it
        // is reported as its own state, with a retry, so a genuine admin is not
        // told they have been removed because the network blinked.
        setState({
          status: 'denied',
          userId: user.id,
          email,
          deniedReason: 'check-failed',
          errorMessage: error instanceof Error ? error.message : String(error),
        })
      }
    }

    const db = getAvexaClient()

    generation.current += 1
    const initial = generation.current
    setState(LOADING)

    void db.auth
      .getSession()
      .then(({ data }) => resolve(data.session?.user ?? null, initial))
      .catch((error: unknown) => {
        if (disposed || generation.current !== initial) return
        setState({
          status: 'unconfigured',
          userId: null,
          email: null,
          deniedReason: null,
          errorMessage: error instanceof Error ? error.message : String(error),
        })
      })

    // SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED and USER_UPDATED all arrive here.
    // A token refresh re-runs the admin check on purpose: an admin revoked
    // mid-session should lose the panel at the next refresh, not at the next
    // reload.
    const { data: subscription } = db.auth.onAuthStateChange((_event, session) => {
      generation.current += 1
      const mine = generation.current
      if (session?.user) setState(LOADING)
      void resolve(session?.user ?? null, mine)
    })

    return () => {
      disposed = true
      subscription.subscription.unsubscribe()
    }
  }, [recheckToken])

  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    if (!isAvexaConfigured()) {
      throw new AdminSignInError('not-configured', 'Supabase is not configured')
    }

    // `@avexa/api` wraps PostgREST, not GoTrue, so there is no named function to
    // route this through — auth is the one place a screen talks to supabase-js
    // directly. It is still the shared client, so session handling cannot drift.
    const { error } = await getAvexaClient().auth.signInWithPassword({
      email: email.trim(),
      password,
    })

    if (error) throw new AdminSignInError(classifySignInError(error), error.message)
    // No navigation here. `onAuthStateChange` fires, the admin check runs, and
    // the route guard moves the operator — so a non-admin who signs in with a
    // perfectly valid password lands on "no access" rather than being bounced
    // back to a form that just accepted their password.
  }, [])

  const signOut = useCallback(async (): Promise<void> => {
    generation.current += 1
    setState(SIGNED_OUT)
    // EVERY CACHED ROW GOES WITH THE SESSION. The panel's queries hold user
    // emails, phone numbers and moderation notes; leaving them in memory means
    // the next person to sign in on this machine sees the previous operator's
    // data flash on screen before the first refetch replaces it. `clear()`
    // rather than `invalidateQueries()` — invalidation keeps the stale data and
    // merely marks it for refetch, which is exactly the leak.
    queryClient.clear()
    try {
      await getAvexaClient().auth.signOut()
    } catch (error) {
      // The local session is already gone, which is what the operator asked for.
      console.warn('[auth] sign-out failed on the server', error)
    }
  }, [queryClient])

  const recheck = useCallback((): void => {
    setRecheckToken((value) => value + 1)
  }, [])

  const value = useMemo<AdminSessionValue>(
    () => ({ ...state, signIn, signOut, recheck }),
    [state, signIn, signOut, recheck],
  )

  return <AdminSessionContext.Provider value={value}>{children}</AdminSessionContext.Provider>
}
